/**
 * New survey / Edit (1.7.3): the title and one line of purpose in English and Spanish, who it asks (name + apartment,
 * or nobody — counts only), the optional email box, the days it takes answers, and up to ten questions of four kinds,
 * each in both languages, in the order the office puts them (Move up / Move down, by the keyboard, said aloud).
 * On Edit every question and choice sends its id back: the answers are kept by it. What the server refuses (a
 * question changed after answers exist) is shown in the server's words.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { QUESTION_TYPES, SURVEY_MAX_CHOICES, SURVEY_MAX_QUESTIONS, SurveyInputSchema, type QuestionType, type SurveyAsks, type SurveyDef, type SurveyDetail, type SurveyInput } from '@shared/surveys';
import { useAnnounce } from '@/components/Announcer';
import { ApiRequestError } from '@/lib/api';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { translateSurveyWords, createSurvey, editSurvey, useSurveyRefresh } from './api';
import '@/styles/surveys173.css';

interface ChoiceDraft {
  key: number;
  id?: string;
  en: string;
  es: string;
}
interface QuestionDraft {
  key: number;
  id?: string;
  type: QuestionType;
  en: string;
  es: string;
  choices: ChoiceDraft[];
}

const picks = (type: QuestionType) => type === 'one' || type === 'many';
const toIso = (k: string | null) => (k && /^\d{8}$/.test(k) ? `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}` : '');
const toKey = (iso: string) => (iso ? iso.replace(/-/g, '') : null);

export function SurveyEditor({
  existing,
  hasAnswers = false,
  onClose,
  onSaved,
  returnFocusTo,
}: {
  /** Edit: the survey as it is. Absent: a new survey. */
  existing?: SurveyDef;
  /** Edit: answers exist already — say what can still change. */
  hasAnswers?: boolean;
  onClose: () => void;
  onSaved: (d: SurveyDetail) => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useSurveyRefresh();
  const nextKey = useRef(1);
  const key = () => nextKey.current++;
  const blankChoice = (): ChoiceDraft => ({ key: key(), en: '', es: '' });
  const blankQuestion = (): QuestionDraft => ({ key: key(), type: 'one', en: '', es: '', choices: [blankChoice(), blankChoice()] });

  const [titleEn, setTitleEn] = useState(existing?.title.en ?? '');
  const [titleEs, setTitleEs] = useState(existing?.title.es ?? '');
  const [purposeEn, setPurposeEn] = useState(existing?.purpose.en ?? '');
  const [purposeEs, setPurposeEs] = useState(existing?.purpose.es ?? '');
  const [asks, setAsks] = useState<SurveyAsks>(existing?.asks ?? 'named');
  const [email, setEmail] = useState(existing?.email ?? true);
  const [open, setOpen] = useState(toIso(existing?.openDayKey ?? null));
  const [close, setClose] = useState(toIso(existing?.closeDayKey ?? null));
  const [questions, setQuestions] = useState<QuestionDraft[]>(() =>
    existing ? existing.questions.map((q) => ({ key: key(), id: q.id, type: q.type, en: q.text.en, es: q.text.es, choices: q.choices.map((c) => ({ key: key(), id: c.id, en: c.en, es: c.es })) })) : [blankQuestion()],
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** Where the focus goes once the page has drawn again (a moved question keeps its button; a new one gets its box). */
  const focusNext = useRef<string | null>(null);
  useEffect(() => {
    const id = focusNext.current;
    if (!id) return;
    focusNext.current = null;
    document.getElementById(id)?.focus();
  });

  const full = questions.length >= SURVEY_MAX_QUESTIONS;
  const patch = (i: number, p: Partial<QuestionDraft>) => setQuestions((qs) => qs.map((q, k) => (k === i ? { ...q, ...p } : q)));
  const patchChoice = (i: number, j: number, p: Partial<ChoiceDraft>) => setQuestions((qs) => qs.map((q, k) => (k === i ? { ...q, choices: q.choices.map((c, m) => (m === j ? { ...c, ...p } : c)) } : q)));

  const setType = (i: number, type: QuestionType) => {
    const q = questions[i]!;
    // the two pick kinds start with two choices; the words typed for a choice are kept while the kind is tried out
    patch(i, { type, choices: picks(type) && q.choices.length < 2 ? [...q.choices, ...Array.from({ length: 2 - q.choices.length }, blankChoice)] : q.choices });
  };
  const addQuestion = () => {
    if (full) return;
    focusNext.current = `sv-q-${questions.length}-en`;
    setQuestions((qs) => [...qs, blankQuestion()]);
    announce(t('surveys.editor.added', { n: questions.length + 1 }));
  };
  /**
   * Careful translation: the Spanish for every English box whose Spanish box is still empty, in one call. It only
   * fills the boxes — a person reads them, corrects them and presses Save.
   */
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState<string | null>(null);
  const translate = async () => {
    const slots: { en: string; put: (es: string) => void }[] = [];
    const want = (en: string, es: string, put: (v: string) => void) => {
      if (en.trim() && !es.trim()) slots.push({ en: en.trim(), put });
    };
    want(titleEn, titleEs, setTitleEs);
    want(purposeEn, purposeEs, setPurposeEs);
    questions.forEach((q, i) => {
      want(q.en, q.es, (es) => patch(i, { es }));
      if (picks(q.type)) q.choices.forEach((c, j) => want(c.en, c.es, (es) => patchChoice(i, j, { es })));
    });
    if (!slots.length) {
      const m = t('surveys.editor.translateNothing');
      setTranslated(m);
      announce(m);
      return;
    }
    setTranslating(true);
    setTranslated(null);
    try {
      const r = await translateSurveyWords(slots.map((x) => x.en));
      r.es.forEach((es, k) => slots[k]?.put(es));
      const m = t(r.offline ? 'surveys.editor.translatedOffline' : 'surveys.editor.translatedDone', { count: slots.length });
      setTranslated(m);
      announce(m);
    } catch (e) {
      const m = e instanceof ApiRequestError && e.message ? e.message : t('surveys.editor.translateFailed');
      setTranslated(m);
      announce(m, 'assertive');
    } finally {
      setTranslating(false);
    }
  };

  const removeQuestion = (i: number) => {
    focusNext.current = questions.length > 1 ? `sv-q-${Math.min(i, questions.length - 2)}-en` : 'sv-add-question';
    setQuestions((qs) => qs.filter((_, k) => k !== i));
    announce(t('surveys.editor.removed', { n: i + 1 }));
  };
  const move = (i: number, by: -1 | 1) => {
    const to = i + by;
    if (to < 0 || to >= questions.length) return;
    setQuestions((qs) => {
      const out = [...qs];
      const [q] = out.splice(i, 1);
      out.splice(to, 0, q!);
      return out;
    });
    // the same button of the same question, unless it is now at the edge (then the other one)
    const wanted = by === -1 ? (to === 0 ? 'down' : 'up') : to === questions.length - 1 ? 'up' : 'down';
    focusNext.current = `sv-q-${to}-${wanted}`;
    announce(t(by === -1 ? 'surveys.editor.movedUp' : 'surveys.editor.movedDown', { n: i + 1 }));
  };
  const addChoice = (i: number) => {
    const q = questions[i]!;
    if (q.choices.length >= SURVEY_MAX_CHOICES) return;
    focusNext.current = `sv-q-${i}-c-${q.choices.length}-en`;
    patch(i, { choices: [...q.choices, blankChoice()] });
  };
  const removeChoice = (i: number, j: number) => {
    const q = questions[i]!;
    if (q.choices.length <= 2) return;
    focusNext.current = `sv-q-${i}-c-${Math.min(j, q.choices.length - 2)}-en`;
    patch(i, { choices: q.choices.filter((_, m) => m !== j) });
    announce(t('surveys.editor.choiceRemoved', { n: j + 1, q: i + 1 }));
  };

  /** What is missing, in words, and the box to go to. */
  const problemOf = (): { message: string; focus: string } | null => {
    if (!titleEn.trim()) return { message: t('surveys.editor.needTitle'), focus: 'sv-title-en' };
    if (!questions.length) return { message: t('surveys.editor.needQuestion'), focus: 'sv-add-question' };
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i]!;
      if (!q.en.trim()) return { message: t('surveys.editor.needQuestionText', { n: i + 1 }), focus: `sv-q-${i}-en` };
      if (!picks(q.type)) continue;
      const j = q.choices.findIndex((c) => !c.en.trim());
      if (j >= 0) return { message: t('surveys.editor.needChoiceText', { n: i + 1, c: j + 1 }), focus: `sv-q-${i}-c-${j}-en` };
      if (q.choices.length < 2) return { message: t('surveys.editor.needTwoChoices', { n: i + 1 }), focus: `sv-q-${i}-add-choice` };
    }
    if (open && close && close < open) return { message: t('surveys.editor.closeBeforeOpen'), focus: 'sv-close' };
    return null;
  };

  const save = async () => {
    const p = problemOf();
    if (p) {
      setProblem(p.message);
      announce(p.message, 'assertive');
      document.getElementById(p.focus)?.focus();
      return;
    }
    const body: SurveyInput = {
      title: { en: titleEn, es: titleEs },
      purpose: { en: purposeEn, es: purposeEs },
      asks,
      email: asks === 'named' && email,
      openDayKey: toKey(open),
      closeDayKey: toKey(close),
      questions: questions.map((q) => ({
        ...(q.id ? { id: q.id } : {}),
        type: q.type,
        text: { en: q.en, es: q.es },
        choices: picks(q.type) ? q.choices.map((c) => ({ ...(c.id ? { id: c.id } : {}), en: c.en, es: c.es })) : [],
      })),
    };
    if (!SurveyInputSchema.safeParse(body).success) {
      const m = t('surveys.editor.invalid');
      setProblem(m);
      announce(m, 'assertive');
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      const d = existing ? await editSurvey(existing.slug, body) : await createSurvey(body);
      const m = t(existing ? 'surveys.editor.saved' : 'surveys.editor.created', { title: d.survey.title.en });
      toast(m);
      announce(m);
      refresh(d.survey.slug);
      onSaved(d);
      onClose();
    } catch (err) {
      // 409 has-answers and the rest: the server's own words
      const m = errorText(err);
      setProblem(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetContent
      wide
      title={existing ? t('surveys.editor.editTitle') : t('surveys.editor.newTitle')}
      returnFocusTo={returnFocusTo}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-testid="sv-cancel">
            {t('surveys.editor.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => save()} data-testid="sv-save">
            {busy ? t('surveys.editor.saving') : t('surveys.editor.save')}
          </Button>
        </>
      }
    >
      <div className="sv-editor" data-testid="sv-editor">
        {existing && hasAnswers ? (
          <p className="warn" data-testid="sv-has-answers">
            <Icon name="info" />
            <span>{t('surveys.editor.hasAnswers')}</span>
          </p>
        ) : null}

        <div className="grid-2">
          <div className="field">
            <label htmlFor="sv-title-en">
              {t('surveys.editor.titleEn')}
              <span className="req">
                {' '}
                <span aria-hidden="true">*</span>
                <span className="sr-only">{t('surveys.editor.required')}</span>
              </span>
            </label>
            <input id="sv-title-en" className="input" lang="en" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} maxLength={120} aria-required="true" autoComplete="off" data-testid="sv-title-en" />
          </div>
          <div className="field">
            <label htmlFor="sv-title-es">{t('surveys.editor.titleEs')}</label>
            <input id="sv-title-es" className="input" lang="es" value={titleEs} onChange={(e) => setTitleEs(e.target.value)} maxLength={120} autoComplete="off" aria-describedby="sv-es-help" data-testid="sv-title-es" />
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="sv-purpose-en">{t('surveys.editor.purposeEn')}</label>
            <input id="sv-purpose-en" className="input" lang="en" value={purposeEn} onChange={(e) => setPurposeEn(e.target.value)} maxLength={200} autoComplete="off" aria-describedby="sv-purpose-help" data-testid="sv-purpose-en" />
          </div>
          <div className="field">
            <label htmlFor="sv-purpose-es">{t('surveys.editor.purposeEs')}</label>
            <input id="sv-purpose-es" className="input" lang="es" value={purposeEs} onChange={(e) => setPurposeEs(e.target.value)} maxLength={200} autoComplete="off" aria-describedby="sv-es-help" data-testid="sv-purpose-es" />
          </div>
        </div>
        <p className="help" id="sv-purpose-help">
          {t('surveys.editor.purposeHelp')}
        </p>
        <p className="help" id="sv-es-help">
          {t('surveys.editor.esHelp')}
        </p>

        <fieldset className="field">
          <legend className="eyebrow">{t('surveys.editor.asks')}</legend>
          <div className="radio-cards">
            <label>
              <input type="radio" name="sv-asks" checked={asks === 'named'} onChange={() => setAsks('named')} data-testid="sv-asks-named" />
              <span>
                {t('surveys.editor.asksNamed')}
                <small>{t('surveys.editor.asksNamedHelp')}</small>
              </span>
            </label>
            <label>
              <input type="radio" name="sv-asks" checked={asks === 'anonymous'} onChange={() => setAsks('anonymous')} data-testid="sv-asks-anonymous" />
              <span>
                {t('surveys.editor.asksAnonymous')}
                <small>{t('surveys.editor.asksAnonymousHelp')}</small>
              </span>
            </label>
          </div>
        </fieldset>

        {asks === 'named' ? (
          <div className="field">
            <label className="switch">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} aria-describedby="sv-email-help" data-testid="sv-email" />
              <span>{t('surveys.editor.email')}</span>
            </label>
            <p className="help" id="sv-email-help">
              {t('surveys.editor.emailHelp')}
            </p>
          </div>
        ) : null}

        <div className="grid-2">
          <div className="field">
            <label htmlFor="sv-open">{t('surveys.editor.opens')}</label>
            <input id="sv-open" className="input" type="date" value={open} onChange={(e) => setOpen(e.target.value)} aria-describedby="sv-dates-help" data-testid="sv-open" />
          </div>
          <div className="field">
            <label htmlFor="sv-close">{t('surveys.editor.closes')}</label>
            <input id="sv-close" className="input" type="date" value={close} onChange={(e) => setClose(e.target.value)} aria-describedby="sv-dates-help" data-testid="sv-close" />
          </div>
        </div>
        <p className="help" id="sv-dates-help">
          {t('surveys.editor.datesHelp')}
        </p>

        <section aria-labelledby="h-sv-questions" style={{ display: 'grid', gap: 14 }}>
          <h3 id="h-sv-questions" style={{ margin: 0 }}>
            {t('surveys.editor.questions', { count: questions.length, max: SURVEY_MAX_QUESTIONS })}
          </h3>
          <ol className="plain-list" style={{ display: 'grid', gap: 14 }}>
            {questions.map((q, i) => (
              <li key={q.key}>
                <fieldset className="sv-q" data-testid={`sv-q-${i}`}>
                  <legend>{t('surveys.editor.questionN', { n: i + 1 })}</legend>
                  <div className="field">
                    <label htmlFor={`sv-q-${i}-type`}>{t('surveys.editor.kind')}</label>
                    <select id={`sv-q-${i}-type`} className="input" value={q.type} onChange={(e) => setType(i, e.target.value as QuestionType)} data-testid={`sv-q-${i}-type`}>
                      {QUESTION_TYPES.map((k) => (
                        <option key={k} value={k}>
                          {/* 1.8.2 (D-1.8.2-4): "Pick several (check all that apply)" */}
                          {k === 'many' ? t('ev182.pickSeveral') : t(`surveys.kind.${k}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid-2">
                    <div className="field">
                      <label htmlFor={`sv-q-${i}-en`}>{t('surveys.editor.questionEn')}</label>
                      <input id={`sv-q-${i}-en`} className="input" lang="en" value={q.en} onChange={(e) => patch(i, { en: e.target.value })} maxLength={300} aria-required="true" autoComplete="off" data-testid={`sv-q-${i}-en`} />
                    </div>
                    <div className="field">
                      <label htmlFor={`sv-q-${i}-es`}>{t('surveys.editor.questionEs')}</label>
                      <input id={`sv-q-${i}-es`} className="input" lang="es" value={q.es} onChange={(e) => patch(i, { es: e.target.value })} maxLength={300} autoComplete="off" data-testid={`sv-q-${i}-es`} />
                    </div>
                  </div>
                  {picks(q.type) ? (
                    <div role="group" aria-label={t('surveys.editor.choicesOf', { n: i + 1 })} style={{ display: 'grid', gap: 10 }}>
                      <span className="eyebrow">{t('surveys.editor.choices')}</span>
                      {q.choices.map((c, j) => (
                        <div className="sv-choice" key={c.key}>
                          <div className="field">
                            <label htmlFor={`sv-q-${i}-c-${j}-en`}>{t('surveys.editor.choiceEn', { c: j + 1 })}</label>
                            <input id={`sv-q-${i}-c-${j}-en`} className="input" lang="en" value={c.en} onChange={(e) => patchChoice(i, j, { en: e.target.value })} maxLength={120} aria-required="true" autoComplete="off" data-testid={`sv-q-${i}-c-${j}-en`} />
                          </div>
                          <div className="field">
                            <label htmlFor={`sv-q-${i}-c-${j}-es`}>{t('surveys.editor.choiceEs', { c: j + 1 })}</label>
                            <input id={`sv-q-${i}-c-${j}-es`} className="input" lang="es" value={c.es} onChange={(e) => patchChoice(i, j, { es: e.target.value })} maxLength={120} autoComplete="off" data-testid={`sv-q-${i}-c-${j}-es`} />
                          </div>
                          <Button variant="secondary" size="sm" disabled={q.choices.length <= 2} onClick={() => removeChoice(i, j)} aria-label={t('surveys.editor.removeChoiceN', { c: j + 1, n: i + 1 })} data-testid={`sv-q-${i}-c-${j}-remove`}>
                            {t('surveys.editor.removeChoice')}
                          </Button>
                        </div>
                      ))}
                      <div className="chips dense" style={{ alignItems: 'center' }}>
                        <Button variant="secondary" size="sm" disabled={q.choices.length >= SURVEY_MAX_CHOICES} onClick={() => addChoice(i)} aria-label={t('surveys.editor.addChoiceN', { n: i + 1 })} data-testid={`sv-q-${i}-add-choice`}>
                          <Icon name="plus" /> {t('surveys.editor.addChoice')}
                        </Button>
                        <span className="help">{q.choices.length >= SURVEY_MAX_CHOICES ? t('surveys.editor.choicesFull', { max: SURVEY_MAX_CHOICES }) : t('surveys.editor.choicesHelp', { max: SURVEY_MAX_CHOICES })}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="help">{t(q.type === 'short' ? 'surveys.editor.shortHelp' : 'surveys.editor.yesnoHelp')}</p>
                  )}
                  <div className="chips dense">
                    <Button id={`sv-q-${i}-up`} variant="secondary" size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('surveys.editor.moveUpN', { n: i + 1 })} data-testid={`sv-q-${i}-up`}>
                      {t('surveys.editor.moveUp')}
                    </Button>
                    <Button id={`sv-q-${i}-down`} variant="secondary" size="sm" disabled={i === questions.length - 1} onClick={() => move(i, 1)} aria-label={t('surveys.editor.moveDownN', { n: i + 1 })} data-testid={`sv-q-${i}-down`}>
                      {t('surveys.editor.moveDown')}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => removeQuestion(i)} aria-label={t('surveys.editor.removeQuestionN', { n: i + 1 })} data-testid={`sv-q-${i}-remove`}>
                      {t('surveys.editor.removeQuestion')}
                    </Button>
                  </div>
                </fieldset>
              </li>
            ))}
          </ol>
          <div className="chips dense" style={{ alignItems: 'center' }}>
            <Button id="sv-add-question" variant="secondary" disabled={full} onClick={addQuestion} aria-describedby="sv-add-help" data-testid="sv-add-question">
              <Icon name="plus" /> {t('surveys.editor.addQuestion')}
            </Button>
            <span className="help" id="sv-add-help" data-testid="sv-add-help">
              {full ? t('surveys.editor.questionsFull', { max: SURVEY_MAX_QUESTIONS }) : t('surveys.editor.questionsLeft', { count: SURVEY_MAX_QUESTIONS - questions.length })}
            </span>
          </div>
        </section>

        <section aria-labelledby="sv-translate-h">
          <h3 id="sv-translate-h" className="eyebrow">
            {t('surveys.editor.translateTitle')}
          </h3>
          <div className="chips dense" style={{ alignItems: 'center' }}>
            <Button variant="secondary" disabled={translating} onClick={() => translate()} aria-describedby="sv-translate-help" data-testid="sv-translate">
              {translating ? t('surveys.editor.translating') : t('surveys.editor.translate')}
            </Button>
            <span className="help" id="sv-translate-help">
              {t('surveys.editor.translateHelp')}
            </span>
          </div>
          {translated ? (
            <p className="help" role="status" data-testid="sv-translate-msg">
              {translated}
            </p>
          ) : null}
        </section>

        {problem ? (
          <p className="field-err" role="alert" data-testid="sv-msg">
            <Icon name="alert" width={16} height={16} /> {problem}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}
